import React, { useState, useEffect, useMemo } from 'react';
import './_category.scss'
import { Collapse, FormGroup, ControlLabel, Modal, Button } from 'react-bootstrap';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import CategoryField from './Field';
import {
  servicesGetCategoryNames,
  servicesUpdateCategory,
  servicesCreateCategory,
} from '../../../../../../services/endpoints/services';
import { getErrorMessage } from '../../../helpers/getters';
import { delay } from '../../../../../../utils/helper';
import { getModalTitle, getIcon } from './helpers';
import ModalPreloader from '../../../../../../components/Modal/Preloader';
import CountDown from '../../../../../../components/CountDown';
import Tooltip from '../../../../../../components/Tooltip';
import { editCategory, createCategory } from '../../../reducer';
import { getCategoriesList } from '../../../../../../redux/actions/services';
import Loader from '../../../../../../components/Loaders/Loader';
import SelectIcon from '../../../../../../components/SelectIcon';
import { ICON_TYPES } from '../../../../../../components/SelectIcon/constants';

const ModalEditCategory = ({ modalData, serviceDispatch, onClose }) => {
  const dispatch = useDispatch();
  const { config } = useSelector((state) => state.services);

  const { register, handleSubmit } = useForm();
  const [modal, setModal] = useState(null)
  const [form, setForm] = useState({
    init: false,
    isLoading: false,
    success: false,
    error: null,
    open: false,
    categoryNames: [],
  })
  const [icon, setIcon] = useState(getIcon(modalData));
  const onChangeIcon = (value) => {
    setIcon(value);
  }
  const modalTitle = useMemo(() => getModalTitle(modal), [modal]);
  const { init, isLoading, categoryNames, open, error } = form;
  const mainCategoryName = categoryNames.length ? categoryNames[0] : null;
  const otherCategoryNames = categoryNames.length && categoryNames.length > 1 ? categoryNames.filter((description, index) => index !== 0) : [];
  const isAutoClose = init && !mainCategoryName;
  const isCreateCategory = !modal;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getCategoryNames = async () => {
    try {
      const id = modal ? modal.id : null;
      const response = await servicesGetCategoryNames(id);
      await delay(500)
      if (response && response.data) {
        setForm((prevState) => ({
          ...prevState,
          init: true,
          categoryNames: response.data,
        }));
      }
    } catch (error) {
      await delay(500)
      const errorMessage = getErrorMessage(error);
      return setForm((prevState) => ({
        ...prevState,
        error: errorMessage,
        init: true,
        isLoading: false,
      }))
    }
  };

  useEffect(() => {
    setModal(modalData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (modal === modalData) {
      getCategoryNames()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal])

  const onOpenOtherLanguages = () => {
    setForm((prevState) => ({
      ...prevState,
      open: !prevState.open,
    }))
  };

  const onSubmit = async (data) => {
    const { name, position } = data;
    let categoryId = null;

    setForm((prevState) => ({
      ...prevState,
      error: null,
      isLoading: true,
    }));

    try {
      if (isCreateCategory) {
        const response = await servicesCreateCategory(name, position, icon);
        if (response?.data) {
          categoryId = response.data.id;
        }
      } else {
        await servicesUpdateCategory(modal.id, name, icon);
        categoryId = modal.id;
      }
      if (categoryId) {
        await dispatch(getCategoriesList());
        await setForm((prevState) => ({
          ...prevState,
          success: true,
        }));
        await delay(500);
        if (isCreateCategory) {
          return serviceDispatch(createCategory(categoryId));
        }
        return serviceDispatch(editCategory(categoryId));
      }
      throw new Error(`categoryId ${typeof categoryId} - ${categoryId}`);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      return setForm((prevState) => ({
        ...prevState,
        error: errorMessage,
        isLoading: false,
      }))
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
    >
      <Modal.Header closeButton={!isLoading && init}>
        <Modal.Title>{modalTitle}</Modal.Title>
      </Modal.Header>
      <ModalPreloader
        isLoading={!init}
      >
        <Modal.Body>
          {error
            ? (
              <div className={classNames('alert alert-danger', {
                'alert-single': !mainCategoryName,
              })}
              >
                {error}
              </div>
            ) : null}
          {isLoading ? <Loader success={form.success} /> : null }
          {mainCategoryName
            ? (
              <div className="services-categories">
                <div className="services-categories__row">
                  <div className="services-categories__row-icon">
                    <SelectIcon
                      types={[ICON_TYPES.ICON, ICON_TYPES.IMAGE, ICON_TYPES.EMOJI]}
                      extensions={config?.icons?.extensions}
                      icon={icon}
                      modal={modalData}
                      onChange={onChangeIcon}
                    />
                  </div>
                  <div className="services-categories__row-form">
                    <div className={`services-categories__${mainCategoryName.direction} services-categories__default-translation`}>
                      {otherCategoryNames.length
                        ? (
                          <div className="services-categories__collapse">
                            <span
                              className="collapse-link"
                              onClick={onOpenOtherLanguages}
                            >
                              {open ? 'Hide' : 'Show'}
                              {' '}
                              translations (
                              {otherCategoryNames.length}
                              )
                            </span>
                          </div>
                        ) : null}
                      <CategoryField
                        register={register}
                        direction={mainCategoryName.direction}
                        languageName={mainCategoryName.language_name}
                        languageCode={mainCategoryName.language_code}
                        value={mainCategoryName.value}
                      />
                    </div>
                  </div>
                </div>
                {otherCategoryNames.length
                  ? (
                    <Collapse in={open}>
                      <div>
                        {otherCategoryNames.map((name, index) => (
                          <CategoryField
                            key={index}
                            register={register}
                            direction={name.direction}
                            languageName={name.language_name}
                            languageCode={name.language_code}
                            value={name.value}
                          />
                        ))}
                      </div>
                    </Collapse>
                  ) : null}
                {isCreateCategory
                  ? (
                    <FormGroup>
                      <ControlLabel>
                        Position
                        {' '}
                        <Tooltip
                          placement="top"
                          text={(
                            <p>
                              The position of a service category
                              <br />
                              {' '}
                              after adding it
                            </p>
                          )}
                          tooltipClass="tooltip__top"
                        >
                          <span className="fas fa-exclamation-circle" />
                        </Tooltip>
                      </ControlLabel>
                      <select
                        className="form-control"
                        name="position"
                        defaultValue="bottom"
                        {...register('position')}
                      >
                        <option value="bottom">Bottom</option>
                        <option value="top">Top</option>
                      </select>
                    </FormGroup>
                  ) : null}
              </div>
            ) : null}
        </Modal.Body>
        <Modal.Footer className="custom__modal-footer">
          {mainCategoryName
            ? (
              <Button
                type="submit"
                bsStyle="primary"
                disabled={isLoading}
              >
                Save changes
              </Button>
            ) : null}
          <Button
            disabled={isLoading}
            onClick={onClose}
          >
            Close
            {' '}
            {isAutoClose ? (
              <>
                (
                <CountDown seconds={5} onComplete={onClose} />
                )
              </>
            ) : null}
          </Button>
        </Modal.Footer>
      </ModalPreloader>
    </form>
  );
};

ModalEditCategory.propTypes = {
  modalData: PropTypes.any,
  serviceDispatch: PropTypes.func,
  onClose: PropTypes.func,
}

export default ModalEditCategory;
